<template>
  <div class="container payment-updater-container">
    <b-modal
      :visible="!isLoading && items && !items.length"
      id="rebill-items-modal"
      centered
      hide-footer
      hide-header
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-row class="alert-messages">
        <div class="alert-container" v-bind:class="{'alert-container-without-margin': !session}">
          <div>Oops! It appears you currently don’t have any subscriptions eligible for reactivation.</div>
        </div>
      </b-row>
      <b-row class="my-3">
        <b-col class="text-center">
          <b-button
            @click="logout()"
            class="btn py-2 px-5"
            variant="primary"
          >Continue</b-button>
        </b-col>
      </b-row>
    </b-modal>
    <b-alert show variant="danger" class="text-center">
      <b>
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']"></font-awesome-icon>&nbsp;
        Action required:
      </b>
      <span class="text-dp">Please update your payment method on file.</span>
      <span class="text-mb">Update payment method.</span>
    </b-alert>
    <div class="payment-updater-wrapper">
      <b-card no-body class="payment-updater-card summary">
        <b-card-header role="button" class="summary-header d-flex align-items-center d-md-none" @click="toggleSummary($event)">
          <b-card-text class="mb-0">
            <span class="align-items-center">Show Summary <font-awesome-icon class="icon-mb ml-1" :icon="['fas', 'angle-down']"></font-awesome-icon></span>
            <span class="align-items-center">Hide Summary <font-awesome-icon class="icon-mb ml-1" :icon="['fas', 'angle-up']"></font-awesome-icon></span>
          </b-card-text>
          <b-card-text class="rebill-item-total">{{order_total_info.total_amount | currency(currency)}}</b-card-text>
        </b-card-header>
        <b-card-header class="summary-header d-none d-md-flex">
          <b-card-text class="d-flex align-items-center">
            Summary
          </b-card-text>
        </b-card-header>
        <b-card-body class="summary-details py-3" v-if="items && items.length">
          <div v-for="(item, index) in items" :key="'item-'+index.toString()" class="summary-detail">
            <b-card-text class="due-date">
              Due {{getHumanReadableDate(item.order.created)}}
            </b-card-text>
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-if="items.length > 1"
                v-model="item.selected"
                @change="calculateTotals()"
                :value="true"
                :unchecked-value="false"
                @click.native.stop
                class="check mx-2 my-0"
              ></b-form-checkbox>
              <b-card-text class="item-info">
                <span><span class="name">{{ item.name }}</span><span class="quantity-separator">x</span>{{item.quantity}}</span>
                <span>{{item.unit_price | currency(currency)}}</span>
              </b-card-text>
            </div>
            <hr v-if="index + 1 != items.length" />
          </div>
        </b-card-body>
        <b-card-footer class="summary-details px-3 py-4">
          <div v-if="order_total_info.total_discount > 0" class="d-flex align-items-center justify-content-between rebill-discount">
            <b-card-text>Discount</b-card-text>
            <b-card-text class="mb-3">-{{order_total_info.total_discount | currency(currency)}}</b-card-text>
          </div>
          <div class="d-flex align-items-center justify-content-between rebill-total">
            <b-card-text class="mb-0">Total</b-card-text>
            <b-card-text class="mb-0">{{order_total_info.total_amount | currency(currency)}}</b-card-text>
          </div>
        </b-card-footer>
      </b-card>
      <b-card no-body class="mb-2 payment-updater-card form">
        <b-card-header>
          <b-card-text class="heading">Update Your Payment Method</b-card-text>
          <b-card-text class="sub-heading">Your subscription will resume, and you’ll only be billed for the selected product<span v-if="selectedItemsCount > 1">s</span>.</b-card-text>
        </b-card-header>
        <b-card-body class="text-left">
          <b-form @submit.prevent="submit()" class="credit-card-form">
            <b-row class="card-details">
              <b-col class="customer-match-formgroup col-pd col-pd-right">
                <b-form-group label="Card number*" label-for="number" class="mb-0 card-info">
                  <font-awesome-icon :icon="cardBrandClass" class="left-icon"></font-awesome-icon>
                  <b-form-input id="number" type="tel" ref="number" class="form-control w-100 checkout-form-input px-5"
                                :data-error="(cardErrors.number) ? true : false"
                                placeholder="1234 1234 1234 2378" :maxlength="19"
                                v-model.trim="number" v-cardformat:formatCardNumber>
                  </b-form-input>
                  <div class="right-icon">
                    <img id="safe-and-secure" :src="require('@/assets/images/lock.svg')"/>
                    <b-tooltip target="safe-and-secure" triggers="hover" placement="top">
                      All payments are Safe and Secure
                    </b-tooltip>
                  </div>
                </b-form-group>
                <div v-if="cardErrors.number" class="text-danger input-error text-right pr-1">
                  <small>{{ cardErrors.number }}</small>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="customer-match-formgroup col-pd col-6">
                <b-form-group class="mb-0" label="Expiration date*" label-for="expiry">
                  <b-form-input id="expiry" ref="cardExpInput" class="form-control checkout-form-input"
                                :data-error="(cardErrors.expiry) ? true : false" placeholder="MM/YYYY"
                                v-model.trim="expiry" v-cardformat:formatCardExpiry />
                  <div v-if="cardErrors.expiry" class="text-danger input-error text-right pr-1">
                    <small>{{ cardErrors.expiry }}</small>
                  </div>
                </b-form-group>
              </b-col>
              <b-col class="customer-match-formgroup col-pd-right col-6">
                <b-form-group label="Security code*" label-for="cvv_code" class="mb-0 card-info">
                  <b-form-input type="tel" class="checkout-form-input" id="cvv_code" ref="cvv_code" :data-error="(cardErrors.cvv_code) ? true : false"
                              placeholder="CVV" pattern="\d*"
                              v-model.trim="cvv_code" v-cardformat:formatCardCVC>
                  </b-form-input>
                  <div class="right-icon">
                    <img id="help" :src="require('@/assets/images/help.svg')"/>
                    <b-tooltip target="help" triggers="hover" placement="top">
                      3 or 4 Digits usually found on the back of your card
                    </b-tooltip>
                  </div>
                </b-form-group>
                <div v-if="cardErrors.cvv_code" class="text-danger input-error text-right pr-4">
                  <small>{{ cardErrors.cvv_code }}</small>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="customer-match-formgroup col-pd col-pd-right">
                <b-form-group class="mb-0">
                  <b-form-checkbox
                    id="billShipSame"
                    class="billing-information mb-0"
                    v-model="billShipSame"
                    name="billShipSame"
                    :value="true"
                    :unchecked-value="false"
                  >
                    Billing same as shipping information
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <template v-if="!billShipSame">
              <b-row class="customer-match-formgroup mb-md-0">
                <b-col class="pr-0 col-pd mb-4 col-12 col-md-6">
                  <label for="address1" v-if="isIE">Address</label>
                  <b-form-group class="mb-0" label="Address" label-for="address1">
                    <div data-for="address1" class="placeholder-label">Address</div>
                    <b-form-input id="address1" type="text" v-model.trim="customer.address1" placeholder="Street Address"
                                  class="customer checkout-form-input" :disabled="billShipSame"></b-form-input>
                    <div v-if="fieldErrors.address1" class="text-danger input-error text-right pr-1">
                      <small>{{ fieldErrors.address1 }}</small>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col class="col-12 col-pd col-md-6 col-pd-right">
                  <label for="address2" v-if="isIE">Address 2</label>
                  <b-form-group class="mb-0" label="Address 2" label-for="address2">
                    <div data-for="address2" class="placeholder-label">Address 2</div>
                    <b-form-input id="address2" type="text" v-model.trim="customer.address2" placeholder="Street Address 2"
                                  class="customer checkout-form-input" :disabled="billShipSame"></b-form-input>
                    <div v-if="fieldErrors.address2" class="text-danger input-error text-right pr-1">
                      <small>{{ fieldErrors.address2 }}</small>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col class="pr-0 col-pd mb-4 col-12 col-md-6">
                  <label for="city" v-if="isIE">City</label>
                  <b-form-group class="mb-0" label="City" label-for="city">
                    <div data-for="city" class="placeholder-label">City</div>
                    <b-form-input id="city" type="text" v-model.trim="customer.city" placeholder="City"
                                  class="customer checkout-form-input" :disabled="billShipSame"></b-form-input>
                    <div v-if="fieldErrors.city" class="text-danger input-error text-right pr-1">
                      <small>{{ fieldErrors.city }}</small>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col class="col-12 col-pd col-md-6 col-pd-right">
                  <label for="country" v-if="isIE">Country</label>
                  <b-form-group class="dropdown mb-0" label="Country" label-for="country">
                    <div data-for="country" class="placeholder-label">Country</div>
                    <b-form-select id="country" v-model="customer.country" :options="countries" placeholder="Country"
                                   class="customer checkout-form-input" :disabled="billShipSame" @change="countryChanged"></b-form-select>
                    <div v-if="fieldErrors.country" class="text-danger input-error text-right pr-1">
                      <small>{{ fieldErrors.country }}</small>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row class="customer-match-formgroup">
                <b-col class="pr-0 col-pd col-6">
                  <div>
                    <label for="state" v-if="isIE">State</label>
                    <b-form-group class="dropdown mb-0" label="Province/State" label-for="state">
                      <div data-for="state" class="placeholder-label">State</div>
                      <b-form-select v-if="isUsOrCA()" id="state" v-model="customer.state"
                                     :options="getStates()" placeholder="Province/State"
                                     class="customer checkout-form-input" :disabled="billShipSame"></b-form-select>
                      <b-form-input v-if="!isUsOrCA()" id="state" type="text"
                                    v-model.trim="customer.state" placeholder="State" class="customer checkout-form-input"
                      ></b-form-input>
                      <div v-if="fieldErrors.state" class="text-danger input-error text-right pr-1">
                        <small>{{ fieldErrors.state }}</small>
                      </div>
                    </b-form-group>
                  </div>
                </b-col>
                <b-col class="col-pd-right col-6">
                  <label for="zip" v-if="isIE">Postal Code</label>
                  <b-form-group class="mb-0" label="Postal Code" label-for="zip">
                    <div data-for="zip" class="placeholder-label">Postal Code</div>
                    <b-form-input id="zip" :formatter="zipFormat" type="text" v-model.trim="customer.zip" placeholder="Postal Code"
                                  class="customer checkout-form-input" :disabled="billShipSame"></b-form-input>
                    <div v-if="fieldErrors.zip" class="text-danger input-error text-right pr-1">
                      <small>{{ fieldErrors.zip }}</small>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
            </template>
            <b-row>
              <b-col class="col-pd col-pd-right d-flex flex-column">
                <p v-if="!selectedItemsCount" class="text-danger">* Please select at least one item to continue</p>
                <b-button block
                  class="btn"
                  type="submit"
                  variant="primary"
                  :disabled="!isValid()"
                >
                  Update <span v-if="items && items.length > 0 && order_total_info.total_amount > 0">& Pay {{order_total_info.total_amount | currency(currency)}}</span>
                </b-button>
              </b-col>
            </b-row>
          </b-form>
          <div class="d-flex justify-content-center privacy-container order-4">
            <img class="privacy-container-img" :src="require('@/assets/images/Secure-Connection.svg')"/>
          </div>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {getHumanReadableDate, isTestCard, formatZipField, getCountryStates, isCountryUSOrCA, updateRequiredFields,
  getCountries, getFieldLabel, isFieldRequired} from '@/utils'
import axios from 'axios'

export default {
  name: 'PaymentUpdater',
  props: {
    session: Object,
    input: Object,
    rebill: {
      type: Boolean,
      default: function() {
        return false
      }
    }
  },
  data: function() {
    return {
      number: null,
      expiry: null,
      cvv_code: null,
      is_default: false,
      billShipSame: true,
      items: [],
      order_total_info: {
        total_discount: 0,
        total_amount: 0
      },
      cardErrors: {},
      cardBrand: null,
      customer: {
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        first_name: null,
        last_name: null,
        company: null,
        country: 'US'
      },
      fieldErrors: {},
      getHumanReadableDate
    }
  },
  computed: {
    ...mapState(['isIE', 'captchaLoaded', 'isLoading', 'campaign']),
    countries() {
      return getCountries(this.session)
    },
    cardBrandClass() {
      return this.getBrandClass(this.cardBrand)
    },
    selectedItemsCount() {
      let selectedItems = []
      if (this.items && this.items.length > 0) {
        selectedItems = this.items.filter((item) => item.selected) || []
      }
      return selectedItems.length
    },
    currency: function() {
      return (this.items && this.items.length > 0) ? this.items[0].order.currency : 'USD'
    }
  },
  watch: {
    // Note: since CVC can be 3 OR 4 digits we don't watch it
    number: function(val) {
      if (val !== null) {
        let number = val.replace(/\s/g, '')
        if (number && number.length > 12) {
          if (this.$cardFormat.validateCardNumber(val) || isTestCard(val, this.session, this.customer)) {
            delete this.cardErrors['number']
          } else {
            this.cardErrors.number = 'Invalid card number'
          }
        }
      }
    },
    expiry: function(val) {
      if (val !== null) {
        if (this.expiry) {
          this.cardErrors.expiry = null
          if (!this.$cardFormat.validateCardExpiry(val)) {
            this.cardErrors.expiry = 'Invalid expiration date'
          } else {
            delete this.cardErrors['expiry']
          }
        }
      }
    },
    cvv_code: function(val) {
      if (val !== null) {
        if (val === '' || val.trim() === '') {
          this.cardErrors.cvv_code = 'Invalid Security code'
        } else if (val.length > 2) {
          delete this.cardErrors['cvv_code']
        }
      }
    }
  },
  methods: {
    isValid() {
      return (
        !!this.selectedItemsCount &&
        Object.keys(this.cardErrors).length === 0 &&
        this.number && this.expiry && this.cvv_code && this.cvv_code.length > 2
      )
    },
    getFieldLabel: function(field, defaultLabel) {
      return getFieldLabel(field, defaultLabel, this.customer.country)
    },
    isFieldRequired: function(field) {
      return isFieldRequired(field, this.customer.country)
    },
    logout: function(val) {
      this.$store.commit('logout')
    },
    calculateTotals: function() {
      const selectedItems = this.items.filter((item) => item.selected)
      let totalPrice = 0
      let totalDiscount = 0
      for (let item of selectedItems) {
        totalPrice += +item.total_price
        totalDiscount += +item.rebill_discount
      }
      this.order_total_info = {
        total_amount: (totalPrice - totalDiscount).toFixed(2),
        total_discount: totalDiscount.toFixed(2)
      }
    },
    toggleSummary: function($event) {
      const summaryEl = $event.target.closest('.summary')
      if (summaryEl) {
        const summaryDetails = summaryEl.getElementsByClassName('summary-details')
        const summaryHeaders = summaryEl.getElementsByClassName('summary-header')
        for (let summaryDetail of summaryDetails) {
          if (summaryDetail.classList.contains('show')) {
            summaryDetail.classList.remove('show')
          } else {
            summaryDetail.classList.add('show')
          }
        }
        for (let summaryHeader of summaryHeaders) {
          if (summaryHeader.classList.contains('show')) {
            summaryHeader.classList.remove('show')
          } else {
            summaryHeader.classList.add('show')
          }
        }
      }
    },
    submit: function() {
      if (this.validate()) {
        return
      }
      const expiryDate = this.expiry.split('/')
      let year = expiryDate[1].trim()
      // Allow for year shortcut
      if (((year != null ? year.length : undefined) === 2) && /^\d+$/.test(year)) {
        let prefix = (new Date()).getFullYear()
        prefix = prefix.toString().slice(0, 2)
        year = prefix + year
      }

      let params = {
        number: this.number,
        exp_month: +expiryDate[0],
        exp_year: +year,
        cvv_code: this.cvv_code,
        is_default: this.rebill,
        is_rebill: this.rebill
      }
      this.$store.commit('busy')
      this.$emit('submit', this.input, {
        card: params,
        updatePaymentInfo: true,
        items: this.items.filter((item) => item.selected)
      })
      if (this.billShipSame && this.session) {
        this.customer['first_name'] = this.session.customer['ship_first_name']
        this.customer['last_name'] = this.session.customer['ship_last_name']
        this.customer['address1'] = this.session.customer['ship_address1']
        this.customer['city'] = this.session.customer['ship_city']
        this.customer['state'] = this.session.customer['ship_state']
        this.customer['zip'] = this.session.customer['ship_zip']
      } else {
        this.customer['first_name'] = this.session.customer['first_name']
        this.customer['last_name'] = this.session.customer['last_name']
      }
      this.$store.dispatch('update_customer', this.customer)
        .then(customer => {
          console.log(customer)
        })
        .catch(err => {
          if (err.response.status === 400) {
            this.$store.commit('error', err)
          } else {
            this.$store.commit('critical_error', err)
          }
        })
    },
    goBack: function() {
      this.$emit('back')
    },
    validate: function() {
      this.cardErrors = {}
      let hasError = false
      // validate card number
      if (!this.$cardFormat.validateCardNumber(this.number) && !isTestCard(this.number, this.session, this.customer)) {
        this.cardErrors.number = 'Invalid card number'
        hasError = true
      }
      // validate card CVC
      if (!this.$cardFormat.validateCardCVC(this.cvv_code)) {
        this.cardErrors.cvv_code = 'Invalid Security code'
        hasError = true
      }

      // validate card Expiration date
      if (!this.$cardFormat.validateCardExpiry(this.expiry)) {
        this.cardErrors.expiry = 'Invalid expiration date'
        hasError = true
      }

      if (!this.billShipSame) {
        let requiredFields = [
          'address1',
          'city',
          'zip',
          'country'
        ]

        updateRequiredFields(requiredFields, this.customer.country)

        let existingFieldsErrors = {...this.fieldErrors}
        requiredFields.forEach(field => {
          let value = this.customer[field]
          existingFieldsErrors[field] = null
          if (!value || value.length < 1) {
            existingFieldsErrors[field] = 'This field is required.'
            hasError = true
          }
        })
        this.fieldErrors = existingFieldsErrors
      }

      return hasError
    },
    getBrandClass: function(brand) {
      let cardBrandToClass = {
        'jcb': ['fab', 'cc-jcb'],
        'amex': ['fab', 'cc-amex'],
        'visa': ['fab', 'cc-visa'],
        'unknown': ['fas', 'credit-card'],
        'discover': ['fab', 'cc-discover'],
        'diners': ['fab', 'cc-diners-club'],
        'mastercard': ['fab', 'cc-mastercard']
      }
      brand = cardBrandToClass[brand] ? brand : 'unknown'
      return cardBrandToClass[brand]
    },
    countryChanged() {
      this.customer.state = ''
      this.customer.zip = ''
    },
    getStates() {
      return getCountryStates(this.customer.country, this.campaign.blacklisted_states)
    },
    isUsOrCA() {
      return isCountryUSOrCA(this.customer.country)
    },
    zipFormat(value) {
      return formatZipField(value, this.customer.country)
    }
  },
  created() {
    this.items = []
    this.total_amount = 0
    this.total_discount = 0
    this.$store.commit('busy')
    const params = {latest_cycle: true}
    axios.get(`/order_items/${this.input.id}/rebill_summary/`, {params: params})
      .then((result) => {
        this.items = (result.data || []).map((item) => {
          item.selected = true
          return item
        })
        this.calculateTotals()
      })
      .catch((err) => {
        if (err && err.response && err.response.status === 400) {
          let errors = []

          Object.keys(err.response.data).forEach(key => {
            errors = errors.concat(err.response.data[key])
          })

          this.$store.commit('error', errors)
        } else {
          this.$store.commit('critical_error', err)
        }
      })
      .finally(() => this.$store.commit('ready'))
  }
}
</script>

<style scoped lang="less">
  .payment-updater-container {
    font-size: 12.5px;
    max-width: 93%;

    //font-size: 14px;

    .text-dp {
      display: none;
    }

    .text-mb {
      display: inline-block;
    }

    .alert-danger {
      border: 1px solid #EF4444;
      color: #333;
      font-size: 1em;
      padding: 0.93em 0.64em;
    }

    .payment-updater-wrapper {
      display: flex;
      flex-direction: column;
    }

    .payment-updater-card {
      box-shadow: 0 1px 5px 2px rgba(0, 0, 0, 0.05);
      border: none !important;
      max-width: 100%;
      width: 100%;
      margin-bottom: 1.5em !important;
      margin-top: 0.25rem;
      align-self: flex-start;

      &.summary {

        .summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom-width: 0;

          p {
            span:first-child {
              display: flex;
            }
            span:last-child {
              display: none;
            }
          }

          &.show {
            border-bottom-width: 1px;

            p {
              span:first-child {
                display: none;
              }
              span:last-child {
                display: flex;
              }
            }
          }

          .icon-mb {
            font-size: 1em;
          }

          .rebill-item-total {
            color: #333;
            font-size: 1.14em;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          }
        }

        .summary-details {
          display: none;
          color: #333;
          font-size: 1em;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          padding: 0;
          flex: 1;

          .summary-detail {
            > * {
              padding-left: 1.25rem;
              padding-right: 1.25rem;
            }
          }

          &.show {
            display: block;
          }

          .item-info {
            font-size: 1em;
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex: 1;

            .name {
              font-weight: 500;
            }

            .quantity-separator {
              font-size: 1em;
              margin-right: 4px;
              margin-left: 4px;
            }
          }
        }
      }

      .card-header {
        background-color: #FAFAFA;
        border-bottom-color: #E4E5E5;
        font-size: 1em;
        font-weight: 400;
        line-height: normal;
        color: #333;
      }

      .card-footer {
        background-color: transparent;
        color: #333;
        font-size: 1em;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        padding: 1.25rem;

        .rebill-discount {
          p:last-child {
            color: #EF4444;
          }
        }

        .rebill-total {
          font-weight: 500;
          font-size: 1.14em;
        }
      }

      &.form {
        .card-header {
          background-color: transparent;
          color: #333;
          font-weight: 400;
          line-height: normal;
          font-style: normal;

          .heading {
            font-size: 1.14em;
            margin-bottom: 10px;
            font-weight: 500;
          }

          .sub-heading {
            font-size: 0.86em;
            color: #5A5A5A;
            line-height: 1.5em;
          }
        }

        label {
          color: #333;
          font-size: 1em;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }

        .ccnumber-icon {
          .input-group-text {
            background-color: transparent;
          }
        }

        .credit-card-form {
          max-width: 100%;

          .customer-match-formgroup {
            margin-bottom: 1.875em;

            &.col-6 {
              &:first-child {
                padding-right: 6px;
              }
              &:last-child {
                padding-left: 6px;
              }
            }
          }

          .card-info {
            > div {
              position: relative;
              display: flex;
              align-items: center;
            }
          }

          .left-icon {
            position: absolute;
            left: 16px;
            font-size: 1.25em;
          }

          .right-icon {
            position: absolute;
            right: 16px;

            img {
              width: 22px;
              height: 22px;
            }
          }

          .custom-control-label {
            padding-left: 0.6em;
            padding-top: 2px;
          }

          .custom-control-label::before,
          .custom-control-label::after{
            top: 0;
            width: 1.2rem;
            height: 1.2rem;
          }

        }

        .privacy-container {
          margin-top: 1.6em;
          .privacy-container-img {
            width: 70%;
          }
        }

        .btn {
          padding: 0.7em 1.625em;
          text-transform: capitalize;
          font-size: 1.125em;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }
      }

      &:last-child {
        margin-right: 0;
      }
    }

    @media only screen and (min-width: 341px) {
      font-size: 13px;
    }

    @media only screen and (min-width: 351px) {
      font-size: 13.5px;
    }

    @media only screen and (min-width: 366px) {
      font-size: 14px;
    }

    @media only screen and (max-width: 575px) {
      .privacy-container .privacy-container-img {
        width: 70%;
      }
    }

    @media only screen and (min-width: 415px) and (max-width:575px) {
      max-width: 100%;
      font-size: 15px;
    }

    @media only screen and (min-width: 501px) and (max-width:767px)  {
      max-width: 100%;
      font-size: 16px;
    }

    @media only screen and (max-width: 767px) {
      .payment-updater-wrapper {
        display: flex;
        flex-direction: column;
      }
    }

    @media only screen and (max-width: 991px) {
      font-size: 15px;
    }

    @media only screen and (min-width: 768px) {
      .payment-updater-container {
        .payment-updater-wrapper {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }

        .text-dp {
          display: inline-block;
        }

        .text-mb {
          display: none;
        }

        .payment-updater-card {
          max-width: 48.5%;
          width: 48.5%;

          &.summary {
            .summary-header {
              font-weight: 500;
            }

            .summary-details {
              display: block;
            }
          }

          &.form {
            .custom-control-label {
              padding-top: 0 !important;
            }
          }
        }

        .privacy-container .privacy-container-img {
          width: 50% !important;
        }
      }
    }

    @media only screen and (min-width: 992px) {
      font-size: 16px;
    }

    @media only screen and (min-width: 1200px) {
      .payment-updater-card {
        &.summary {
          .summary-header {
            font-size: 1.14em;
          }
        }
      }
    }
  }
</style>
